import { PatientDashboard as SidebarPatientDashboard, type ChecklistItem, type OnboardedMedicationInfo as MedicationInfo, MedicationPreferencesDialog, type MedicationOption, VisitsBookingDialog, type VisitsMedicationPreference as MedicationPreference, type BookingProvider as Provider, showToast, AccountDialog, SupportFeedbackDialog, type ExistingAppointment, Loader } from '@joinomu/ui'
import { useAuth } from '@/hooks/useAuth'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { authService, emailService, convertTimeToTimezone, formatTimeInTimezone, detectUserTimezone, getSupabase } from '@joinomu/shared'

// Helper function to get medication image URL based on name/brand
const getMedicationImageUrl = (medicationName: string, brandName: string): string | undefined => {
  const name = medicationName.toLowerCase()
  const brand = brandName?.toLowerCase() || ''
  
  console.log(`🔍 getMedicationImageUrl: Processing "${medicationName}" with brand "${brandName}"`)
  console.log(`🔍 Lowercase: name="${name}", brand="${brand}"`)
  
  // Specific compound medication mappings (flex-dose compounds)
  // Fix: Flex-dose medications have brand_name: null, not 'compound'
  if (name.includes('semaglutide flex-dose')) {
    console.log(`✅ Matched semaglutide flex-dose compound`)
    return '/images/medications/semaglutide 1ml.jpg'
  }
  if (name.includes('tirzepatide forte flex-dose') || name.includes('tirzepatide flex-dose')) {
    console.log(`✅ Matched tirzepatide flex-dose compound`)
    return '/images/medications/Trizepatide 1ml.png'
  }
  
  // Direct brand medication name mappings (name brand medications)
  // Specific check for Wegovy Pill (must come before general wegovy check)
  if (name.includes('wegovy pill') || brand.includes('wegovy pill')) {
    console.log(`✅ Matched Wegovy Pill`)
    return '/images/medications/novo-wegovy-pill.webp'
  }

  if (name.includes('wegovy')) {
    console.log(`✅ Matched wegovy (direct)`)
    return '/images/medications/screenshot-wegovy.webp'
  } else if (name.includes('ozempic')) {
    console.log(`✅ Matched ozempic (direct)`)
    return '/images/medications/screenshot-ozempic.webp'
  } else if (name.includes('zepbound')) {
    console.log(`✅ Matched zepbound (direct)`)
    return '/images/medications/screenshot-zepbound.webp'
  }
  
  // Generic semaglutide medications (compounds use flex-dose image, brands use their specific images)
  if (name.includes('semaglutide')) {
    console.log(`🔍 Processing semaglutide medication`)
    // Compound medications use the semaglutide flex-dose image
    if (name.includes('(compound)') || brand.includes('(compound)') || brand.includes('compound')) {
      console.log(`✅ Matched semaglutide compound (generic)`)
      return '/images/medications/semaglutide 1ml.jpg'
    }
    // Brand medications use their specific brand images
    if (brand.includes('wegovy')) {
      console.log(`✅ Matched semaglutide wegovy brand`)
      return '/images/medications/screenshot-wegovy.webp'
    } else if (brand.includes('ozempic')) {
      console.log(`✅ Matched semaglutide ozempic brand`)
      return '/images/medications/screenshot-ozempic.webp'
    } else {
      console.log(`✅ Matched semaglutide (fallback to wegovy)`)
      return '/images/medications/screenshot-wegovy.webp'
    }
  }
  
  // Generic tirzepatide medications (compounds use flex-dose image, brands use zepbound)
  if (name.includes('tirzepatide')) {
    console.log(`🔍 Processing tirzepatide medication`)
    // Compound medications use the tirzepatide flex-dose image
    if (name.includes('(compound)') || brand.includes('(compound)') || brand.includes('compound')) {
      console.log(`✅ Matched tirzepatide compound (generic)`)
      return '/images/medications/Trizepatide 1ml.png'
    }
    // Brand medications use zepbound as fallback
    console.log(`✅ Matched tirzepatide (fallback to zepbound)`)
    return '/images/medications/screenshot-zepbound.webp'
  }
  
  // JoinOmu Weight Loss Pack - use the company logo
  if (name.includes('joinomu') || name.includes('weight loss pack') || name.includes('weight-loss pack')) {
    console.log(`✅ Matched JoinOmu Weight Loss Pack`)
    return '/images/medications/joinomu-logo.svg'
  }
  
  console.log(`❌ No match found for "${medicationName}" with brand "${brandName}"`)
  return undefined
}

export function PatientDashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Onboarding state - start with loading to prevent flash
  const [isOnboarded, setIsOnboarded] = useState(false)
  const [onboardingStatusLoading, setOnboardingStatusLoading] = useState(true)
  
  // Add onboarding completion tracking with localStorage persistence
  const [onboardingCompletedLocally, setOnboardingCompletedLocally] = useState(false)
  
  // Subscription adjustment state
  const [recentlyAdjustedMedication, setRecentlyAdjustedMedication] = useState<string | null>(null)

  // Handler for subscription adjusted OK button (dismiss the alert)
  const handleSubscriptionAdjustedOk = (medicationId: string) => {
    setRecentlyAdjustedMedication(null)
  }

  // Handler for subscription adjusted View button (navigate to billing)
  const handleSubscriptionAdjustedView = (medicationId: string) => {
    setRecentlyAdjustedMedication(null)
    handleNavigate('Plans & Billing')
  }
  
  // Initialize localStorage persistence after user is loaded
  useEffect(() => {
    if (user?.id) {
      try {
        const stored = localStorage.getItem(`onboarding_completed_${user.id}`)
        if (stored === 'true') {
          console.log('💾 Found stored onboarding completion for user:', user.id)
          setOnboardingCompletedLocally(true)
        } else {
          // Check for saved onboarding step to restore session
          const savedStep = localStorage.getItem(`onboarding_step_${user.id}`)
          if (savedStep) {
            console.log(`💾 Restoring onboarding session: user was on step "${savedStep}"`)
            // Note: Checklist completion is determined by actual payment success and data state,
            // not by localStorage step tracking. Step tracking is only for UX continuity.
          }
        }
      } catch (error) {
        console.error('❌ Failed to read onboarding data from localStorage:', error)
      }
    }
  }, [user?.id])

  // Helper to update checklist completion state based on actual data
  const updateChecklistCompletionState = (hasPaidPlan: boolean, hasMedicationData: boolean, hasAppointmentData: boolean) => {
    setChecklistItems(prev => prev.map(item => {
      if (item.id === 'plan') {
        return { ...item, completed: hasPaidPlan }
      }
      if (item.id === 'medication') {
        return { ...item, completed: hasMedicationData }
      }
      if (item.id === 'appointment') {
        return { ...item, completed: hasAppointmentData }
      }
      return item
    }))
  }

  // Helper to mark onboarding as completed
  const markOnboardingCompleted = async () => {
    if (user?.id) {
      try {
        // Update database flag to permanently mark onboarding complete
        console.log('🔄 Marking onboarding as completed in database...')
        const { error } = await authService.updatePatientOnboardingStatus(user.id, true)
        
        if (error) {
          console.error('❌ Failed to update onboarding status in database:', error)
        } else {
          console.log('✅ Onboarding status updated in database successfully')
        }
        
        // Update localStorage for immediate UI response
        localStorage.setItem(`onboarding_completed_${user?.id}`, 'true')
        // Clear onboarding step tracking since onboarding is complete
        localStorage.removeItem(`onboarding_step_${user.id}`)
        setOnboardingCompletedLocally(true)
        setIsOnboarded(true)
        console.log('✅ Onboarding marked as completed locally and in state')
      } catch (error) {
        console.error('❌ Failed to save onboarding completion:', error)
        // Still update state even if database/localStorage fails
        setOnboardingCompletedLocally(true)
        setIsOnboarded(true)
      }
    }
  }
  const [medicationDialogOpen, setMedicationDialogOpen] = useState(false)
  const [medicationLoading, setMedicationLoading] = useState(false)
  const [availableMedications, setAvailableMedications] = useState<MedicationOption[]>([])
  const [isFreePlan, setIsFreePlan] = useState(false)
  
  // Real data state for post-onboarding - support multiple medications
  const [realMedicationData, setRealMedicationData] = useState<{
    id: string
    medicationId: string
    name: string
    dosage: string
    supply: string
    status: 'pending' | 'approved' | 'denied'
    nextPrescriptionDue?: string
    fulfillmentStatus?: string
    preferenceId?: string
    medicationInfo?: any
    dosageInfo?: any
  }[]>([])
  const [realAppointmentData, setRealAppointmentData] = useState<{
    id: string
    doctorName: string
    visitType: string
    dateTime: string
    status: 'scheduled' | 'active' | 'pending'
    appointmentDate: string
    startTime: string
    providerId: string
    treatmentType: string
    appointmentType: string
  }[]>([])
  
  // Appointment booking dialog state
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false)
  const [medicationPreferences, setMedicationPreferences] = useState<MedicationPreference[]>([])
  
  // Enhanced medication editing state
  const [editingMedication, setEditingMedication] = useState<{
    id?: string
    medicationId?: string
    medicationName?: string
    dosage?: string
  } | undefined>(undefined)
  const [providers, setProviders] = useState<Provider[]>([])
  const [appointmentLoading, setAppointmentLoading] = useState(false)
  
  // Reschedule appointment state
  const [isRescheduleMode, setIsRescheduleMode] = useState(false)
  const [appointmentToReschedule, setAppointmentToReschedule] = useState<ExistingAppointment | undefined>(undefined)
  
  // Patient addresses state
  const [patientAddresses, setPatientAddresses] = useState<any[]>([])
  const [, setIsLoadingAddresses] = useState(false)
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([
    {
      id: 'plan',
      title: 'Select a plan',
      description: 'Choose the healthcare plan that fits your needs',
      completed: false, // Will be completed after successful payment
    },
    {
      id: 'medication',
      title: 'Select medication preferences',
      description: "Select from the medications you're eligible for",
      completed: false, // User must complete this step
    },
    {
      id: 'appointment',
      title: 'Schedule appointment',
      description: 'Book your first visit with a healthcare provider',
      completed: false,
    },
  ])

  // Sample medication and appointment data - in real app this would come from database
  const sampleMedication: MedicationInfo = {
    name: 'Semaglutide (Ozempic)',
    dosage: '0.5mg',
    frequency: 'Once weekly injection',
    status: 'pending',
    category: 'weightloss',
    description: 'GLP-1 receptor agonist for type 2 diabetes and weight management',
    averageResults: {
      weightLoss: '12-15% body weight',
      bloodSugar: '1.5-2.0% HbA1c reduction',
      satisfaction: '85% patient satisfaction'
    }
  }


  // Fetch real data when patient becomes onboarded OR when user loads (to check for existing data)
  useEffect(() => {
    console.log('🔍 useEffect trigger - isOnboarded:', isOnboarded, 'user?.id:', user?.id)
    if (user?.id) {
      console.log('✅ Calling fetchPostOnboardingData to check for existing medication data')
      fetchPostOnboardingData()
    } else {
      console.log('⚠️ Not calling fetchPostOnboardingData - no user?.id')
    }
  }, [user?.id])

  // Also fetch when onboarding completes
  useEffect(() => {
    if (isOnboarded && user?.id) {
      console.log('✅ Onboarding completed - refreshing medication data')
      fetchPostOnboardingData()
    }
  }, [isOnboarded, user?.id])

  // Handle browser visibility changes to prevent blank screen issues
  // But avoid unnecessary reloads during internal navigation
  useEffect(() => {
    let lastNavigationTime = 0
    let wasHiddenForNavigation = false
    
    const handleVisibilityChange = () => {
      const now = Date.now()
      
      if (document.hidden) {
        // Track when the page becomes hidden
        const timeSinceLastNavigation = now - lastNavigationTime
        wasHiddenForNavigation = timeSinceLastNavigation < 1000 // Hidden within 1s of navigation = likely internal nav
        console.log('👁️ Tab became hidden', { wasHiddenForNavigation, timeSinceLastNavigation })
      } else if (user?.id) {
        // Only reload if this was a real tab switch, not internal navigation
        if (!wasHiddenForNavigation) {
          console.log('🔄 Browser tab became visible after real tab switch - refreshing data to prevent blank screen')
          setTimeout(() => {
            fetchPostOnboardingData()
          }, 100)
        } else {
          console.log('✅ Tab became visible after internal navigation - preserving existing data')
        }
        wasHiddenForNavigation = false
      }
    }

    const handleFocus = () => {
      const now = Date.now()
      const timeSinceLastNavigation = now - lastNavigationTime
      
      if (user?.id && timeSinceLastNavigation > 2000) { // Only if no recent navigation
        console.log('🔄 Window gained focus after being away - refreshing data to prevent blank screen') 
        setTimeout(() => {
          fetchPostOnboardingData()
        }, 100)
      } else {
        console.log('✅ Window gained focus after internal navigation - preserving existing data')
      }
    }
    
    // Track navigation events to distinguish from tab switching
    const handleNavigationStart = () => {
      lastNavigationTime = Date.now()
      console.log('🚀 Navigation started at', lastNavigationTime)
    }
    
    // Listen for navigation events
    window.addEventListener('beforeunload', handleNavigationStart)
    window.addEventListener('popstate', handleNavigationStart)
    
    // Listen for visibility and focus changes
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('beforeunload', handleNavigationStart)
      window.removeEventListener('popstate', handleNavigationStart)
    }
  }, [user?.id])

  // Handle payment success only (tracking plan detection moved to fetchPostOnboardingData)
  useEffect(() => {
    const success = searchParams.get('success')
    const sessionId = searchParams.get('session_id')
    
    if (success === 'true' && sessionId) {
      console.log('💳 Payment success detected! Session ID:', sessionId)
      
      // Check if this is a tracking-only plan
      const planType = searchParams.get('plan_type') || searchParams.get('plan')
      const isTrackingPlan = planType === 'tracking' || planType === 'free'
      
      if (isTrackingPlan) {
        console.log('🆓 Tracking-only plan detected - completing onboarding and going directly to dashboard')
        
        // Complete onboarding immediately for tracking plans
        markOnboardingCompleted()
        
        // Clear onboarding step tracking
        if (user?.id) {
          localStorage.removeItem(`onboarding_step_${user.id}`)
        }
        
        // Show tracking plan success message
        showToast({
          title: 'Tracking Plan Active',
          description: 'You can now track your progress and upgrade anytime',
          variant: 'success'
        })
        
        // Clean up URL parameters
        const newParams = new URLSearchParams(searchParams)
        newParams.delete('success')
        newParams.delete('session_id') 
        newParams.delete('plan_type')
        newParams.delete('plan')
        setSearchParams(newParams, { replace: true })
        
        return // Exit early - no further onboarding needed
      }
      
      // Regular paid plan - continue with standard onboarding
      // Update the plan checklist item to completed
      setChecklistItems(prev => prev.map(item => 
        item.id === 'plan' 
          ? { ...item, completed: true }
          : item
      ))
      
      // Show success message
      showToast({
        title: 'Payment Complete',
        variant: 'success'
      })

      // Clean up URL parameters
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('success')
      newParams.delete('session_id')
      setSearchParams(newParams, { replace: true })

      // Re-fetch data after a short delay to allow webhook to process
      // This ensures we get the updated subscription_status and next_prescription_due
      console.log('🔄 Waiting for webhook to process, then refreshing data...')
      setTimeout(() => {
        console.log('🔄 Refreshing patient data after payment success')
        fetchPostOnboardingData()
      }, 2000) // 2 second delay to allow Stripe webhook to complete

      console.log('✅ Payment success handling complete - plan step marked as completed')
    }
  }, [searchParams, setSearchParams, user?.id])

  // Update patient subscription in database
  const updatePatientSubscription = async (userId: string, planType: 'tracking' | 'free') => {
    try {
      console.log(`🔄 Updating patient subscription to ${planType} plan for user:`, userId)
      
      const supabase = getSupabase()
      
      // First, check if patient record exists
      const { data: existingPatient, error: checkError } = await supabase
        .from('patients')
        .select('id, profile_id, subscription_plan')
        .eq('profile_id', userId)
        .single()
      
      if (checkError) {
        console.error('❌ Error checking existing patient:', checkError)
        showToast({
          title: 'Database Check Error', 
          description: 'Could not verify patient record. Please contact support.',
          variant: 'error'
        })
        return
      }
      
      console.log('🔍 Found existing patient record:', existingPatient)
      
      // Update the subscription
      const { data, error } = await supabase
        .from('patients')
        .update({
          subscription_plan: planType,
          subscription_status: 'active',
          subscription_amount: 0, // Tracking plan is free
          subscription_interval: 'month'
        })
        .eq('profile_id', userId)
        .select()
      
      if (error) {
        console.error('❌ Error updating patient subscription:', error)
        console.error('❌ Error details:', JSON.stringify(error, null, 2))
        showToast({
          title: 'Database Update Error',
          description: `Failed to save plan selection: ${error.message}`,
          variant: 'error'
        })
      } else {
        console.log('✅ Patient subscription updated successfully:', data)
        console.log('✅ Updated record count:', data?.length)
        // Note: No success toast for tracking plan - user experience should be seamless
      }
    } catch (error) {
      console.error('❌ Exception updating patient subscription:', error)
      showToast({
        title: 'Unexpected Error',
        description: 'An unexpected error occurred while updating your plan.',
        variant: 'error'
      })
    }
  }

  const loadPatientAddresses = async () => {
    if (!user?.id) return

    setIsLoadingAddresses(true)
    try {
      console.log('🏠 Loading patient addresses for user:', user.id)
      const { data, error } = await authService.getPatientAddresses()
      
      if (error) {
        console.error('❌ Failed to load patient addresses:', error)
        // Only reset addresses if it's a genuine auth error indicating user not found
        // For timeout errors during tab visibility, preserve existing addresses
        if (error.message?.includes('No user ID') || error.message?.includes('not found')) {
          setPatientAddresses([])
        }
        // Otherwise keep existing addresses to prevent shipping address button flicker
      } else {
        console.log('✅ Patient addresses loaded successfully:', data?.length || 0)
        setPatientAddresses(data || [])
      }
    } catch (err) {
      console.error('❌ Error loading patient addresses:', err)
      // Only reset on genuine auth failures, not timeout errors
      if (err instanceof Error && (err.message?.includes('No user ID') || err.message?.includes('not found'))) {
        setPatientAddresses([])
      }
      // Preserve existing addresses for timeout/network errors during tab visibility
    } finally {
      setIsLoadingAddresses(false)
    }
  }

  const fetchPostOnboardingData = async () => {
    if (!user?.id) return

    try {
      console.log('🚀🚀🚀 REFILL BUTTON FIX 2025-12-16 12:00 - Fetching post-onboarding data for user:', user.id)
      
      // Load patient addresses
      await loadPatientAddresses()
      
      // FIRST: Check for URL parameter-based tracking plan detection (takes priority)
      const currentSearchParams = new URLSearchParams(window.location.search)
      const freePlan = currentSearchParams.get('free')
      const trackingPlan = currentSearchParams.get('tracking')
      const plan = currentSearchParams.get('plan')
      
      const isTrackingPlanFromURL = freePlan === 'true' || 
                                   trackingPlan === 'true' || 
                                   plan === 'tracking' || 
                                   plan === 'free'
      
      if (isTrackingPlanFromURL) {
        console.log('🆓 URL tracking plan detected in fetchPostOnboardingData - setting up free plan mode')
        console.log('🔍 Plan parameters:', { freePlan, trackingPlan, plan })
        
        // Set free plan state immediately to prevent onboarding logic
        setIsFreePlan(true)
        setIsOnboarded(true)
        setOnboardingStatusLoading(false)
        
        // Update database with tracking plan subscription
        updatePatientSubscription(user.id, 'tracking')
        
        // Clean up URL parameters
        const newParams = new URLSearchParams(currentSearchParams)
        newParams.delete('free')
        newParams.delete('tracking')
        newParams.delete('plan')
        
        // Update URL without triggering a navigation
        const newUrl = `${window.location.pathname}${newParams.toString() ? '?' + newParams.toString() : ''}`
        window.history.replaceState({}, '', newUrl)
        
        console.log('✅ Tracking plan setup complete - user should see upgrade dashboard')
        return // Exit early to prevent any onboarding logic
      }
      
      // Check if patient has existing subscription plan
      console.log('🔍 Checking existing subscription plan...')
      const supabase = getSupabase()
      const { data: patientData } = await supabase
        .from('patients')
        .select('subscription_plan, subscription_status, stripe_customer_id, stripe_subscription_id, onboarding_completed')
        .eq('profile_id', user.id)
        .single()
      
      if (patientData && (patientData.subscription_plan === 'tracking' || patientData.subscription_plan === 'free')) {
        console.log('✅ Found existing tracking/free plan subscription:', patientData.subscription_plan)
        setIsFreePlan(true)
        setIsOnboarded(true)
        console.log('🔄 Set patient to free plan mode based on database subscription_plan')
      } else {
        console.log('🔍 No existing tracking plan found:', patientData?.subscription_plan)
      }
      
      // Fetch medication preferences
      console.log('🔍 Fetching medication preferences...')
      const medicationResult = await authService.getPatientMedicationPreferences(user.id)
      console.log('🔍 Medication result:', medicationResult)
      
      if (medicationResult.data && medicationResult.data.length > 0) {
        console.log('✅ Setting REAL medication data from Supabase:', medicationResult.data)
        
        // Fetch estimated delivery dates from medication orders for each preference
        const medicationCards = await Promise.all(medicationResult.data.map(async (med: any, index: number) => {
          console.log(`🔍 Processing medication ${index}:`, med)
          console.log(`🔍 Raw medication_id field:`, med.medication_id)
          console.log(`🔍 All medication fields:`, Object.keys(med))
          console.log(`🔍 Medications object:`, med.medications)
          
          // Get the estimated delivery date and fulfillment status from the most recent medication order for this preference
          let estimatedDelivery = null
          let fulfillmentStatus = null
          try {
            const ordersResult = await authService.getOrdersByPreferenceId(med.id)
            if (ordersResult.data && ordersResult.data.length > 0) {
              // Get the most recent order
              const mostRecentOrder = ordersResult.data
                .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]
              
              if (mostRecentOrder) {
                estimatedDelivery = mostRecentOrder.estimated_delivery
                fulfillmentStatus = mostRecentOrder.fulfillment_status
                console.log(`🔍 Found order data for ${med.medications?.name}:`, {
                  estimatedDelivery,
                  fulfillmentStatus,
                  rawOrder: mostRecentOrder
                })
              } else {
                console.log(`⚠️ No recent order found for ${med.medications?.name}, orders:`, ordersResult.data)
              }
            }
          } catch (error) {
            console.log(`⚠️ Could not fetch order data for medication ${med.medications?.name}:`, error)
          }
          
          const medicationCard = {
            id: med.id, // This is the preference ID
            medicationId: med.medication_id, // Keep the original medication ID for lookup
            name: med.medications?.name || 'Unknown Medication',
            dosage: med.preferred_dosage || 'Not specified',
            supply: med.supply_days ? `${med.supply_days} day supply` : '30 day supply',
            supply_days: med.supply_days, // Only use provider-approved supply_days, not default values
            status: med.status || 'pending',
            estimatedDelivery: estimatedDelivery,
            fulfillmentStatus: fulfillmentStatus,
            // 🔧 FIX: Use the camelCase fields that auth service already transformed
            nextPrescriptionDue: med.nextPrescriptionDue, // Use transformed field instead of med.next_prescription_due
            refillRequested: med.refillRequested, // Use transformed field instead of med.refill_requested
            refillRequestedDate: med.refillRequestedDate, // Use transformed field
            visitRequested: med.visitRequested, // Use transformed field
            visitRequestedDate: med.visitRequestedDate, // Use transformed field
            approvalDate: med.approvalDate, // Use transformed field
            // Provider ID mapping for prescription due alert scheduling
            providerId: med.providerId, // Use camelCase field from auth service transformation
            provider_id: med.provider_id, // Snake case fallback for database compatibility
            // Add additional fields for editing
            preferenceId: med.id,
            medicationInfo: med.medications,
            dosageInfo: med.medication_dosages, // This now contains the SELECTED dosage with stripe_price_id
            // Add subscription fields from patient data
            subscription_status: patientData?.subscription_status,
            stripe_subscription_id: patientData?.stripe_subscription_id
          }
          // 🔍 DEBUG: Confirm camelCase fields are properly set
          console.log(`🔍 REFILL BUTTON FIX DEBUG for ${med.medications?.name}:`)
          console.log(`🔍 Auth service refillRequested (camelCase):`, med.refillRequested)
          console.log(`🔍 Auth service refill_requested (snake_case):`, med.refill_requested)
          console.log(`🔍 Final medicationCard refillRequested:`, medicationCard.refillRequested)
          console.log(`📋 Final medication card for ${med.medications?.name}:`, medicationCard)
          return medicationCard
        }))
        console.log('✅ Final medication cards to display:', medicationCards)
        setRealMedicationData(medicationCards)
      } else {
        console.log('⚠️ No real medication preferences found - medicationResult:', medicationResult)
        console.log('⚠️ Using sample data for testing - this should NOT happen if data exists in Supabase')
        // Only use sample data if no real data exists
        setRealMedicationData([])
      }

      // Fetch scheduled appointments (only if not on tracking plan)
      let appointmentResult: { data: any[] | null, error?: any } = { data: null }
      if (!(patientData && (patientData.subscription_plan === 'tracking' || patientData.subscription_plan === 'free'))) {
        console.log('🔍 Fetching upcoming appointments...')
        appointmentResult = await authService.getPatientAppointments(user.id, true)
        console.log('🔍 Upcoming appointment result:', appointmentResult)
      } else {
        console.log('⚠️ Skipping appointment fetch - patient on tracking plan')
      }
      
      if (appointmentResult.data && appointmentResult.data.length > 0) {
        console.log(`✅ Found ${appointmentResult.data.length} upcoming scheduled appointments for patient`)
        const appointmentCards = appointmentResult.data.map((appointment: any, index: number) => {
          console.log(`🔍 Processing appointment ${index}:`, appointment)
          console.log(`🔍 Raw appointment data for appointment ${index}:`, JSON.stringify(appointment, null, 2))
          console.log(`🔍 Raw appointment keys:`, Object.keys(appointment))
          // Try different potential provider ID field names - add more fallback options
          const providerId = appointment.provider_id || 
                            appointment.provider_profile_id || 
                            appointment.assigned_provider_id || 
                            appointment.provider_internal_id ||
                            appointment.providerid ||
                            appointment.provider_uuid ||
                            'b692a67a-1356-43a8-a753-af4dd00fbf1d' // Fallback to known weight loss provider ID
          
          console.log(`🔍 Final providerId for appointment ${index}:`, providerId)
          
          // Convert appointment time from provider timezone to patient timezone
          const patientTimezone = appointment.patient_timezone || detectUserTimezone()
          const providerTimezone = appointment.providers?.profiles?.timezone || appointment.provider_timezone || 'America/New_York'
          
          let displayTime = appointment.start_time
          // Always perform timezone conversion (don't require both timezones to exist)
          // This ensures patient always sees time in their timezone
          try {
            const convertedTime = convertTimeToTimezone(
              appointment.start_time,
              providerTimezone,
              patientTimezone,
              appointment.appointment_date
            )
            displayTime = formatTimeInTimezone(convertedTime, patientTimezone, '12h')
            console.log(`🌍 APPOINTMENT TIMEZONE: ${appointment.start_time} ${providerTimezone} -> ${displayTime} ${patientTimezone}`)
          } catch (error) {
            console.warn('⚠️ Timezone conversion failed, using original time:', error)
            displayTime = appointment.start_time
          }
          
          // Format date as MM/DD/YYYY using manual parsing to avoid timezone issues
          const [year, month, day] = appointment.appointment_date.split('-').map(Number)
          const appointmentDate = new Date(year, month - 1, day) // month is 0-indexed
          const formattedDate = appointmentDate.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit', 
            year: 'numeric'
          })
          
          // Format time as 12-hour format (displayTime may already be converted)
          let formattedTime = displayTime
          if (displayTime && displayTime.includes(':') && !displayTime.match(/[AP]M/i)) {
            // Only format if it doesn't already have AM/PM
            try {
              const timeParts = displayTime.split(':')
              const hours = timeParts[0]
              const minutes = timeParts[1]
              
              if (hours && minutes) {
                const hour = parseInt(hours, 10)
                const min = parseInt(minutes, 10)
                
                if (!isNaN(hour) && !isNaN(min)) {
                  const ampm = hour >= 12 ? 'PM' : 'AM'
                  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
                  formattedTime = `${hour12}:${min.toString().padStart(2, '0')} ${ampm}`
                } else {
                  console.warn('⚠️ Invalid time parts:', { hours, minutes, hour, min })
                }
              } else {
                console.warn('⚠️ Invalid time format:', displayTime)
              }
            } catch (error) {
              console.warn('⚠️ Time formatting error:', error, 'for time:', displayTime)
            }
          }

          const appointmentCard = {
            id: appointment.id, // Use id field from database
            doctorName: appointment.provider_name || `Dr. Provider ${appointment.provider_id?.slice(0,8)}`,
            visitType: appointment.appointment_type || 'Initial Consultation',
            dateTime: `${formattedDate} at ${formattedTime}`,
            status: appointment.status || 'scheduled',
            appointmentDate: appointment.appointment_date, // Keep original YYYY-MM-DD format for dialog
            formattedDate: formattedDate, // Add formatted version for display
            startTime: formattedTime, // Use fully formatted time
            providerId: appointment.provider_id || providerId, // Use provider_id from database first
            treatmentType: appointment.treatment_type || 'weight_loss',
            appointmentType: appointment.appointment_type || 'consultation'
          }
          
          console.log(`🔍 Final appointment card ${index}:`, appointmentCard)
          return appointmentCard
        })
        console.log('✅ Setting all appointment data:', appointmentCards)
        setRealAppointmentData(appointmentCards)
      } else {
        console.log('⚠️ No upcoming scheduled appointments found')
        setRealAppointmentData([])
      }

      // Auto-set onboarded status with improved logic and persistence
      const hasMedicationData = medicationResult.data && medicationResult.data.length > 0
      const hasAppointmentData = appointmentResult.data && appointmentResult.data.length > 0
      
      // Check if patient has tracking/free plan subscription
      const hasTrackingPlan = patientData && (patientData.subscription_plan === 'tracking' || patientData.subscription_plan === 'free')
      
      // IMPORTANT: Don't override state if user is already set as free plan (from URL parameter detection)
      if (isFreePlan) {
        console.log('🔒 User already set as free plan - preserving state and skipping onboarding logic')
        return
      }
      
      // Check if onboarding was previously completed - prioritize database flag over localStorage
      const wasOnboardingCompleted = patientData?.onboarding_completed || onboardingCompletedLocally
      
      // If database shows completed but localStorage doesn't, update localStorage for consistency
      if (patientData?.onboarding_completed && !onboardingCompletedLocally) {
        console.log('💾 Database shows onboarding completed - syncing localStorage')
        localStorage.setItem(`onboarding_completed_${user.id}`, 'true')
        setOnboardingCompletedLocally(true)
      }
      
      // Check if payment was completed (plan step in checklist)
      const planStepCompleted = checklistItems.find(item => item.id === 'plan')?.completed || false
      
      // Determine if user should be considered onboarded
      const shouldBeOnboarded = (
        patientData?.onboarding_completed || // Database flag takes precedence - once marked complete, always complete
        wasOnboardingCompleted || // Previously completed onboarding (localStorage fallback)
        (hasMedicationData && hasAppointmentData) || // Has complete data (both medication AND appointment required for first-time onboarding)
        hasTrackingPlan // Has tracking plan - only tracking/free plans can skip onboarding without appointments
        // NOTE: Database onboarding_completed flag ensures patients never return to onboarding after deleting appointments
      )
      
      if (shouldBeOnboarded) {
        if (hasTrackingPlan) {
          console.log('✅ Patient has tracking/free plan - setting as onboarded for tracking experience', {
            medications: hasMedicationData,
            appointments: hasAppointmentData,
            subscription_plan: patientData.subscription_plan,
            wasOnboardingCompleted
          })
          setIsFreePlan(true)
          setIsOnboarded(true)
        } else {
          console.log('✅ Patient should be onboarded - setting as onboarded', {
            medications: hasMedicationData,
            appointments: hasAppointmentData,
            databaseCompleted: patientData?.onboarding_completed,
            wasOnboardingCompleted,
            reason: patientData?.onboarding_completed ? 'database flag shows completed' : 
                    wasOnboardingCompleted ? 'previously completed (localStorage)' : 
                    'has both medication data and scheduled appointments'
          })
          markOnboardingCompleted()
        }
        
        // Update checklist completion state based on actual data (for both tracking and paid plans)
        updateChecklistCompletionState(true, Boolean(hasMedicationData), Boolean(hasAppointmentData))
      } else {
        console.log('⚠️ Patient onboarding in progress - keeping in onboarding mode', {
          medications: hasMedicationData,
          appointments: hasAppointmentData,
          subscription_plan: patientData?.subscription_plan,
          wasOnboardingCompleted,
          reason: !hasTrackingPlan ? 'subscription plans require both medication and appointment data' : 'missing required data'
        })
        setIsOnboarded(false)
        
        // Update checklist completion state based on actual data
        // Include planStepCompleted to preserve payment success state even if subscription_plan isn't updated yet
        const hasPaidPlan = Boolean(hasTrackingPlan || (patientData?.subscription_plan && patientData.subscription_plan !== 'none') || planStepCompleted)
        updateChecklistCompletionState(hasPaidPlan, Boolean(hasMedicationData), Boolean(hasAppointmentData))
      }
      
      // Finished determining onboarding status
      setOnboardingStatusLoading(false)
    } catch (error) {
      console.error('❌ Error fetching post-onboarding data:', error)
      // Even on error, stop loading to show something
      setOnboardingStatusLoading(false)
    }
  }

  const handleLogout = async () => {
    try {
      console.log('🔄 Patient dashboard: Starting sign out...')
      await authService.signOut()
      console.log('✅ Patient dashboard: Sign out successful, navigating to home')
      navigate('/')
    } catch (error) {
      console.error('❌ Patient dashboard: Error signing out:', error)
      // Force navigation even if sign out fails
      navigate('/')
    }
  }

  const handleChecklistItemClick = async (item: ChecklistItem) => {
    console.log('Checklist item clicked:', item)
    
    // Prevent multiple simultaneous calls
    if (appointmentLoading) {
      console.log('⚠️ Already loading, ignoring click')
      return
    }
    
    // SEQUENTIAL ONBOARDING: Enforce step order
    const currentItems = checklistItems
    const planCompleted = currentItems.find(i => i.id === 'plan')?.completed || false
    const medicationCompleted = currentItems.find(i => i.id === 'medication')?.completed || false
    
    // Step 1: Plan must be completed first
    if (item.id === 'plan') {
      console.log('🚀 Step 1: Navigating to pricing page for plan selection')
      // Save onboarding state before navigation
      if (user?.id) {
        localStorage.setItem(`onboarding_step_${user.id}`, 'plan')
      }
      navigate('/pricing')
      return
    }
    
    // Step 2: Medication can only be accessed after plan is completed
    if (item.id === 'medication') {
      if (!planCompleted) {
        showToast({
          title: 'Please complete step 1: Select a plan first',
          variant: 'warning'
        })
        console.log('⚠️ Sequential onboarding: Plan must be completed before medication selection')
        return
      }
      
      // Save onboarding state
      if (user?.id) {
        localStorage.setItem(`onboarding_step_${user.id}`, 'medication')
      }
      
      // Open medication preferences dialog
      try {
        console.log('🔄 Fetching medications...')
        
        // Get patient eligibility first
        let patientEligibility = []
        try {
          const supabase = getSupabase()
          const { data: patientData, error: patientError } = await supabase
            .from('patients')
            .select('Eligibility')
            .eq('profile_id', user?.id)
            .single()
            
          if (patientData && patientData.Eligibility) {
            patientEligibility = patientData.Eligibility
            console.log('🔍 Patient eligibility:', patientEligibility)
          } else {
            console.log('⚠️ No patient eligibility found or patient not found:', patientError)
          }
        } catch (error) {
          console.error('❌ Error fetching patient eligibility:', error)
        }
        
        // Fetch available medications from Supabase
        const result = await authService.getAvailableMedications()
        console.log('🔍 Medications result:', result)
        console.log('🔍 Raw result.data:', JSON.stringify(result.data, null, 2))
        
        if (result.data && result.data.length > 0) {
          console.log('✅ Setting medications:', result.data)
          
          // Filter medications based on patient eligibility
          const eligibleMedications = result.data.filter((med: any) => {
            const medicationEligibility = med.Eligibility // Note: capital E
            console.log(`🔍 Checking medication ${med.name}: eligibility=${medicationEligibility}, patient has: ${patientEligibility}`)
            
            // TRACKING PLAN UPGRADE FIX: If patient has no eligibility but we're in step 2 onboarding
            // (adding medications), show all available medications to enable plan upgrades
            if (!patientEligibility || patientEligibility.length === 0) {
              console.log(`⚠️ No patient eligibility found - checking if this is step 2 onboarding...`)
              
              // Check if we're in the onboarding context (not fully onboarded yet)
              if (!isOnboarded) {
                console.log(`✅ Step 2 onboarding detected - showing all medications for tracking plan upgrades`)
                return true // Show all medications during onboarding for tracking plan upgrades
              }
              
              console.log(`❌ Not in onboarding context - no eligibility means no medications`)
              return false
            }
            
            // Check if medication eligibility matches any of patient's eligibilities
            return patientEligibility.includes(medicationEligibility)
          })
          
          console.log(`✅ Filtered to ${eligibleMedications.length} eligible medications out of ${result.data.length} total`)
          
          // Transform database data to match MedicationOption interface
          const transformedMedications = eligibleMedications.map((med: any, index: number) => {
            console.log(`🔍 Transforming medication ${index}:`, med.name)
            console.log(`🔍 Medication ${index} dosages:`, med.medication_dosages)
            
            const dosages = med.medication_dosages?.map((dosage: any) => {
              console.log(`🔍 Processing dosage:`, dosage)
              return dosage.strength
            }) || []
            
            console.log(`🔍 Final dosages for ${med.name}:`, dosages)
            
            const imageUrl = getMedicationImageUrl(med.name, med.brand_name)
            console.log(`🖼️ Image URL for "${med.name}" (${med.brand_name}):`, imageUrl)
            
            const transformedMed = {
              id: med.id,
              name: med.name,
              description: med.description,
              category: med.category,
              available_dosages: dosages,
              image_url: imageUrl
            }
            console.log(`🔍 Final transformed medication ${index}:`, transformedMed)
            return transformedMed
          })
          
          // Add "Doctor Recommendation" option if no eligible medications
          if (transformedMedications.length === 0) {
            console.log('🏥 No eligible medications found - querying for Doctor Recommendation medication')
            
            // Get the real Doctor Recommendation medication from database
            try {
              const { data: doctorRecMed, error: doctorRecError } = await getSupabase()
                .from('medications')
                .select('id, name, description, medication_dosages(strength)')
                .eq('name', 'Doctor Recommendation')
                .single()
                
              if (doctorRecMed && !doctorRecError) {
                console.log('✅ Found Doctor Recommendation medication:', doctorRecMed)
                transformedMedications.push({
                  id: doctorRecMed.id,
                  name: doctorRecMed.name,
                  description: doctorRecMed.description || 'Schedule a consultation with your provider to discuss medication options',
                  category: 'general',
                  available_dosages: doctorRecMed.medication_dosages?.map((d: any) => d.strength) || ['Provider Assessment'],
                  image_url: undefined
                })
              } else {
                console.log('⚠️ Doctor Recommendation medication not found, using fallback')
                transformedMedications.push({
                  id: 'doctor-recommendation-fallback',
                  name: 'Doctor Recommendation',
                  description: 'Schedule a consultation with your provider to discuss medication options',
                  category: 'general',
                  available_dosages: ['Provider Assessment'],
                  image_url: undefined
                })
              }
            } catch (error) {
              console.log('❌ Error querying Doctor Recommendation medication:', error)
              transformedMedications.push({
                id: 'doctor-recommendation-fallback',
                name: 'Doctor Recommendation',
                description: 'Schedule a consultation with your provider to discuss medication options',
                category: 'general',
                available_dosages: ['Provider Assessment'],
                image_url: undefined
              })
            }
          }
          
          console.log('✅ Final medications list:', transformedMedications)
          setAvailableMedications(transformedMedications)
        } else {
          console.log('❌ No medications data received:', result.error)
          // Still add doctor recommendation as fallback
          setAvailableMedications([{
            id: 'doctor-recommendation-fallback',
            name: 'Doctor Recommendation',
            description: 'Schedule a consultation with your provider to discuss medication options',
            category: 'general',
            available_dosages: ['Provider Assessment'],
            image_url: undefined
          }])
        }
        console.log('🔄 Opening dialog...')
        setMedicationDialogOpen(true)
        console.log('✅ Dialog open state set to true')
        console.log('✅ Dialog should be open now')
      } catch (error) {
        console.error('❌ Failed to load medications:', error)
      }
      return
    }
    
    if (item.id === 'appointment') {
      if (!planCompleted) {
        showToast({
          title: 'Please complete step 1: Select a plan first',
          variant: 'warning'
        })
        console.log('⚠️ Sequential onboarding: Plan must be completed before appointment scheduling')
        return
      }
      if (!medicationCompleted) {
        showToast({
          title: 'Please complete step 2: Select medication preferences first',
          variant: 'warning'
        })
        console.log('⚠️ Sequential onboarding: Medication must be completed before appointment scheduling')
        return
      }
      
      // Save onboarding state
      if (user?.id) {
        localStorage.setItem(`onboarding_step_${user.id}`, 'appointment')
      }
      
      // Open appointment booking dialog
      if (!user?.id) {
        console.error('❌ No user ID available for appointment booking')
        return
      }
      
      try {
        setAppointmentLoading(true) // Set loading state to prevent multiple calls
        
        console.log('🔄 Loading real assigned providers for patient...')
        
        // Try to load real assigned providers - NO FALLBACK to wrong providers
        try {
          console.log('🔍 Attempting to load real assigned providers...')
          const assignedResult = await Promise.race([
            authService.getPatientAssignedProviders(user.id),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Provider loading timeout')), 5000)) // Increased timeout
          ])
          
          if ((assignedResult as any).data && (assignedResult as any).data.length > 0) {
            console.log('✅ Found real assigned providers:', (assignedResult as any).data)
            setProviders((assignedResult as any).data)
          } else {
            console.log('⚠️ No assigned providers found for this patient')
            setProviders([]) // Don't show scheduling if no providers assigned
            showToast({
              title: 'No Providers Assigned',
              description: 'Contact support to be assigned to a healthcare provider',
              variant: 'warning'
            })
          }
        } catch (error) {
          console.log('❌ Provider loading failed:', (error as any).message)
          setProviders([]) // Don't show scheduling if provider loading fails
          showToast({
            title: 'Provider Loading Failed',
            description: 'Unable to load your assigned providers. Please try again.',
            variant: 'error'
          })
        }
        
        // Set default medications first
        const defaultMedications = [
          {
            id: 'default-1',
            medication_name: 'Semaglutide',
            preferred_dosage: '0.5mg',
            status: 'approved' as const,
            treatment_type: 'weight_loss'
          },
          {
            id: 'default-2',
            medication_name: 'Testosterone',
            preferred_dosage: '200mg/ml',
            status: 'approved' as const,
            treatment_type: 'mens_health'
          }
        ]
        setMedicationPreferences(defaultMedications)
        console.log('✅ Set default medications')
        
        // Try to load real medication preferences with timeout
        try {
          console.log('🔍 Attempting to load real medication preferences...')
          const prefsResult = await Promise.race([
            authService.getPatientMedicationPreferences(user.id),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Medication preferences timeout')), 2000))
          ])
          
          if ((prefsResult as any).data) {
            const approvedPrefs = (prefsResult as any).data
              .filter((pref: any) => pref.status === 'approved')
              .map((pref: any) => ({
                id: pref.id,
                medication_name: pref.medication_name,
                preferred_dosage: pref.preferred_dosage,
                status: pref.status as 'approved',
                treatment_type: pref.treatment_type || 'weight_loss'
              }))
            if (approvedPrefs.length > 0) {
              console.log('✅ Found real medication preferences, updating:', approvedPrefs)
              setMedicationPreferences(approvedPrefs)
            } else {
              console.log('⚠️ No approved preferences found, keeping defaults')
            }
          } else {
            console.log('⚠️ No medication preferences data returned, keeping defaults')
          }
        } catch (error) {
          console.log('⚠️ Medication preferences loading failed/timeout, using defaults:', error instanceof Error ? error.message : String(error))
        }
        
        console.log('🔄 Opening appointment dialog with data ready...')
        setAppointmentDialogOpen(true)
        console.log('✅ Dialog state set to open - should be visible now')
        
      } catch (error) {
        console.error('❌ Error in appointment dialog setup:', error)
      } finally {
        setAppointmentLoading(false) // Always reset loading state
      }
      
      return
    }
    
    // Update item as completed for demo purposes
    setChecklistItems(prev => prev.map(i => 
      i.id === item.id ? { ...i, completed: true } : i
    ))

    // Check if all items are completed
    const updatedItems = checklistItems.map(i => 
      i.id === item.id ? { ...i, completed: true } : i
    )
    const allCompleted = updatedItems.every(item => item.completed)
    if (allCompleted) {
      markOnboardingCompleted()
    }
  }


  const handleMedicationAction = () => {
    console.log('Medication action clicked')
    // In real app, would show medication details or update preferences
  }

  const handleEditMedication = (medicationId?: string) => {
    console.log('Edit medication clicked', medicationId)
    
    // If user is on free plan, redirect to pricing instead of showing medication dialog
    if (isFreePlan) {
      console.log('🆓 Free plan user trying to access medications - redirecting to pricing')
      navigate('/pricing')
      return
    }
    
    if (medicationId) {
      // Edit specific medication preference
      const medicationToEdit = realMedicationData.find(med => med.id === medicationId)
      console.log('🔍 PatientDashboard: medicationToEdit found:', medicationToEdit)
      if (medicationToEdit) {
        const editingData = {
          id: medicationToEdit.id, // This is the preference ID
          preferenceId: medicationToEdit.preferenceId, // Preference ID for updates
          medicationId: medicationToEdit.medicationId, // The actual medication ID for lookup
          medicationName: medicationToEdit.name,
          dosage: medicationToEdit.dosage,
          frequency: medicationToEdit.supply,
          status: medicationToEdit.status,
          // Include the full medication info for the dialog
          medicationInfo: medicationToEdit.medicationInfo,
          dosageInfo: medicationToEdit.dosageInfo
        }
        console.log('🔍 PatientDashboard: Setting editingMedication for preference:', editingData)
        setEditingMedication(editingData)
      }
    } else {
      // Add new medication
      setEditingMedication(undefined)
    }
    
    // Load available medications and open dialog
    loadMedicationsAndOpenDialog()
  }

  const loadMedicationsAndOpenDialog = async () => {
    try {
      console.log('🔄 Loading medications for dialog...')
      
      // Get patient eligibility
      let patientEligibility = []
      try {
        const supabase = getSupabase()
        const { data: patientData, error: patientError } = await supabase
          .from('patients')
          .select('Eligibility')
          .eq('profile_id', user?.id)
          .single()
          
        if (patientData && patientData.Eligibility) {
          patientEligibility = patientData.Eligibility
          console.log('🔍 Patient eligibility:', patientEligibility)
        } else {
          console.log('⚠️ No patient eligibility found or patient not found:', patientError)
        }
      } catch (error) {
        console.error('❌ Error fetching patient eligibility:', error)
      }
      
      const result = await authService.getAvailableMedications()
      console.log('🔍 Edit mode medications result:', result)
      console.log('🔍 Edit mode raw result.data:', JSON.stringify(result.data, null, 2))
      
      if (result.data && result.data.length > 0) {
        console.log('✅ Edit mode: Setting medications:', result.data)
        
        // Filter medications based on patient eligibility
        const eligibleMedications = result.data.filter((med: any) => {
          const medicationEligibility = med.Eligibility // Note: capital E
          console.log(`🔍 Checking medication ${med.name}: eligibility=${medicationEligibility}, patient has: ${patientEligibility}`)
          
          // If editing an existing medication, always include the current medication
          if (editingMedication?.medicationId === med.id) {
            console.log(`✅ Including ${med.name} because it's the current medication being edited`)
            return true
          }
          
          // TRACKING PLAN UPGRADE FIX: If patient has no eligibility but we're in step 2 onboarding
          // (adding medications), show all available medications to enable plan upgrades
          if (!patientEligibility || patientEligibility.length === 0) {
            console.log(`⚠️ Edit mode: No patient eligibility found - checking if this is step 2 onboarding...`)
            
            // Check if we're in the onboarding context (not fully onboarded yet)
            if (!isOnboarded) {
              console.log(`✅ Edit mode: Step 2 onboarding detected - showing all medications for tracking plan upgrades`)
              return true // Show all medications during onboarding for tracking plan upgrades
            }
            
            console.log(`❌ Edit mode: Not in onboarding context - no eligibility means only current medication`)
            return false
          }
          
          // Check if medication eligibility matches any of patient's eligibilities
          return patientEligibility.includes(medicationEligibility)
        })
        
        console.log(`✅ Filtered to ${eligibleMedications.length} eligible medications out of ${result.data.length} total`)
        
        // Transform database data to match MedicationOption interface
        const transformedMedications = eligibleMedications.map((med: any, index: number) => {
          console.log(`🔍 Edit mode: Transforming medication ${index}:`, med.name)
          console.log(`🔍 Edit mode: Medication ${index} dosages:`, med.medication_dosages)
          
          const dosages = med.medication_dosages?.map((dosage: any) => {
            console.log(`🔍 Edit mode: Processing dosage:`, dosage)
            return dosage.strength
          }) || []
          
          console.log(`🔍 Edit mode: Final dosages for ${med.name}:`, dosages)
          
          return {
            id: med.id,
            name: med.name,
            description: med.description,
            category: med.category,
            available_dosages: dosages,
            image_url: getMedicationImageUrl(med.name, med.brand_name)
          }
        })
        
        // Add "Doctor Recommendation" option if no eligible medications
        if (transformedMedications.length === 0) {
          console.log('🏥 Edit mode: No eligible medications found - querying for Doctor Recommendation medication')
          
          // Get the real Doctor Recommendation medication from database
          try {
            const { data: doctorRecMed, error: doctorRecError } = await getSupabase()
              .from('medications')
              .select('id, name, description, medication_dosages(strength)')
              .eq('name', 'Doctor Recommendation')
              .single()
              
            if (doctorRecMed && !doctorRecError) {
              console.log('✅ Edit mode: Found Doctor Recommendation medication:', doctorRecMed)
              transformedMedications.push({
                id: doctorRecMed.id,
                name: doctorRecMed.name,
                description: doctorRecMed.description || 'Schedule a consultation with your provider to discuss medication options',
                category: 'general',
                available_dosages: doctorRecMed.medication_dosages?.map((d: any) => d.strength) || ['Provider Assessment'],
                image_url: undefined
              })
            } else {
              console.log('⚠️ Edit mode: Doctor Recommendation medication not found, using fallback')
              transformedMedications.push({
                id: 'doctor-recommendation-fallback',
                name: 'Doctor Recommendation',
                description: 'Schedule a consultation with your provider to discuss medication options',
                category: 'general',
                available_dosages: ['Provider Assessment'],
                image_url: undefined
              })
            }
          } catch (error) {
            console.log('❌ Edit mode: Error querying Doctor Recommendation medication:', error)
            transformedMedications.push({
              id: 'doctor-recommendation-fallback',
              name: 'Doctor Recommendation',
              description: 'Schedule a consultation with your provider to discuss medication options',
              category: 'general',
              available_dosages: ['Provider Assessment'],
              image_url: undefined
            })
          }
        }
        
        console.log('✅ Edit mode: Final medications list:', transformedMedications)
        setAvailableMedications(transformedMedications)
      } else {
        console.log('❌ Edit mode: No medications data received:', result.error)
        // Still add doctor recommendation as fallback
        setAvailableMedications([{
          id: 'doctor-recommendation-fallback',
          name: 'Doctor Recommendation',
          description: 'Schedule a consultation with your provider to discuss medication options',
          category: 'general',
          available_dosages: ['Provider Assessment'],
          image_url: undefined
        }])
      }
      setMedicationDialogOpen(true)
    } catch (error) {
      console.error('❌ Failed to load medications:', error)
    }
  }

  const handleOpenAppointmentDialog = () => {
    console.log('Opening appointment dialog for new appointment')
    
    if (!user?.id) {
      console.error('❌ No user ID available for appointment booking')
      return
    }
    
    // Use the same logic as the checklist appointment booking
    handleChecklistItemClick({ id: 'appointment', title: 'Schedule appointment', description: 'Book your first visit with a healthcare provider', completed: false })
  }

  const handleEditAppointment = async (appointmentId?: string) => {
    console.log('Edit appointment clicked - opening reschedule dialog', appointmentId)
    
    if (!user?.id || !realAppointmentData || realAppointmentData.length === 0) {
      console.error('❌ No user ID or appointment data available for rescheduling')
      return
    }

    try {
      setAppointmentLoading(true)
      
      // Find the specific appointment to reschedule
      let appointmentToReschedule
      if (appointmentId) {
        appointmentToReschedule = realAppointmentData.find(apt => apt.id === appointmentId)
        if (!appointmentToReschedule) {
          console.error('❌ Could not find appointment with ID:', appointmentId)
          return
        }
      } else {
        // If no specific appointment ID, use the first one (fallback)
        appointmentToReschedule = realAppointmentData[0]
      }
      
      console.log('🔍 Selected appointment for rescheduling:', appointmentToReschedule)
      console.log('🔍 Appointment date details:', {
        appointmentDate: appointmentToReschedule.appointmentDate,
        startTime: appointmentToReschedule.startTime,
        doctorName: appointmentToReschedule.doctorName
      })
      
      // Get the correct provider internal ID by looking up the provider by profile_id
      console.log('🔍 Looking up provider by profile_id:', appointmentToReschedule.providerId)
      const providerLookupResult = await authService.getProviderByProfileId(appointmentToReschedule.providerId)
      
      let actualProviderId = appointmentToReschedule.providerId // fallback to profile_id
      if (providerLookupResult.data) {
        actualProviderId = providerLookupResult.data.id // Use the internal provider ID
        console.log('✅ Found provider internal ID:', actualProviderId)
      } else {
        console.log('⚠️ Could not find provider internal ID, using profile_id as fallback')
      }
      
      // Improve the provider name using the provider lookup data
      let providerName = appointmentToReschedule.doctorName
      
      // Try to get a better provider name from the provider lookup
      if (providerLookupResult.data?.first_name && providerLookupResult.data?.last_name) {
        providerName = `Dr. ${providerLookupResult.data.first_name} ${providerLookupResult.data.last_name}`
        console.log('✅ Using provider name from lookup:', providerName)
      } else if (providerLookupResult.data?.name) {
        providerName = providerLookupResult.data.name
        console.log('✅ Using provider name from lookup:', providerName)
      } else {
        console.log('⚠️ Using fallback provider name:', providerName)
      }
      
      // Convert display time back to 24h format for proper parsing
      // The startTime might be in "10:00 AM" format, need to convert to "10:00"
      let originalStartTime = appointmentToReschedule.startTime
      if (originalStartTime.includes('AM') || originalStartTime.includes('PM')) {
        // Convert 12-hour format to 24-hour format
        try {
          const timeParts = originalStartTime.split(' ')
          const time = timeParts[0]
          const period = timeParts[1]
          const [hoursStr, minutesStr] = time.split(':')
          const hours = parseInt(hoursStr, 10)
          const minutes = parseInt(minutesStr, 10)
          
          if (!isNaN(hours) && !isNaN(minutes)) {
            let convertedHours = hours
            if (period === 'PM' && hours !== 12) {
              convertedHours = hours + 12
            } else if (period === 'AM' && hours === 12) {
              convertedHours = 0
            }
            
            originalStartTime = `${convertedHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
            console.log('✅ Converted time format:', appointmentToReschedule.startTime, '->', originalStartTime)
          } else {
            console.warn('⚠️ Invalid time parts in reschedule:', { hoursStr, minutesStr, hours, minutes })
          }
        } catch (error) {
          console.warn('⚠️ Time conversion error in reschedule:', error, 'for time:', originalStartTime)
        }
      }

      const appointmentData = {
        id: appointmentToReschedule.id,
        providerId: actualProviderId,
        providerName: providerName,
        appointmentDate: appointmentToReschedule.appointmentDate,
        formattedDate: (appointmentToReschedule as any).formattedDate, // Include formatted date for dialog display
        startTime: originalStartTime, // Use original time format (24h for booking API)
        displayTime: appointmentToReschedule.startTime, // Keep display time (12h with AM/PM) for dialog
        treatmentType: appointmentToReschedule.treatmentType,
        appointmentType: appointmentToReschedule.appointmentType
      }
      
      console.log('✅ Setting appointment data for reschedule:', appointmentData)
      setAppointmentToReschedule(appointmentData)
      setIsRescheduleMode(true)
      
      // Load providers (reuse existing logic)
      const fallbackProviders = [
        {
          id: actualProviderId,
          name: providerName, // Use the improved provider name
          specialty: 'General',
          profile_id: appointmentToReschedule.providerId,
          treatment_type: appointmentToReschedule.treatmentType,
          is_primary: true
        }
      ]
      setProviders(fallbackProviders)
      
      // Open the dialog in reschedule mode
      setAppointmentDialogOpen(true)
      console.log('✅ Reschedule dialog opened')
    } catch (error) {
      console.error('❌ Error setting up reschedule:', error)
      showToast({
        title: 'Error',
        description: 'An error occurred while preparing to reschedule your appointment',
        variant: 'error'
      })
    } finally {
      setAppointmentLoading(false)
    }
  }

  const handleMedicationPreferencesSubmit = async (preferences: { 
    medicationId: string; 
    dosage: string; 
    isEdit?: boolean; 
    preferenceId?: string 
  }) => {
    if (!user?.id) return

    setMedicationLoading(true)
    try {
      let result
      
      if (preferences.isEdit && editingMedication?.id) {
        // Update existing preference using patient-specific method - this will reset status to pending
        console.log('🔄 Updating existing medication preference (will reset to pending for provider review)')
        result = await authService.updatePatientMedicationPreference(
          editingMedication.id, 
          {
            dosage: preferences.dosage
          }
        )
      } else {
        // Create new preference 
        console.log('🔄 Creating new medication preference (will be pending for provider review)')
        result = await authService.createMedicationPreference({
          medicationId: preferences.medicationId,
          dosage: preferences.dosage,
          userId: user.id
        })
      }
      
      if (result.success) {
        console.log('✅ Medication preference saved successfully')
        
        // For medication edits with existing subscriptions, trigger automatic subscription change
        if (preferences.isEdit && editingMedication?.id) {
          try {
            console.log('🔄 Checking for existing subscription to update...')
            
            // Import StripeService for subscription changes
            const { StripeService } = await import('@joinomu/shared')
            
            // Find the medication dosage information for old and new dosages
            const currentMed = realMedicationData?.find(med => med.id === editingMedication.id)
            const oldDosageId = currentMed?.dosageInfo?.id
            
            // Find the new dosage ID by looking up the medication and dosage in the database
            // We need to query the medication_dosages table since available_dosages is just strings
            let newDosageId = null
            if (preferences.medicationId && preferences.dosage) {
              try {
                const { data: dosageData } = await getSupabase()
                  .from('medication_dosages')
                  .select('id')
                  .eq('medication_id', preferences.medicationId)
                  .eq('strength', preferences.dosage)
                  .single()
                
                newDosageId = dosageData?.id
                console.log('🔍 Found new dosage ID:', newDosageId, 'for strength:', preferences.dosage)
              } catch (dosageLookupError) {
                console.error('❌ Error looking up new dosage ID:', dosageLookupError)
              }
            }
            
            console.log('🔍 Dosage change info:', {
              medicationName: currentMed?.name,
              oldDosageId,
              newDosageId,
              oldDosage: currentMed?.dosage,
              newDosage: preferences.dosage
            })
            
            // Only trigger subscription change if we have the necessary IDs and there's a current subscription
            if (oldDosageId && newDosageId && oldDosageId !== newDosageId && currentMed?.nextPrescriptionDue) {
              console.log('🔄 Triggering automatic subscription change with proration...')
              
              const changeResult = await StripeService.changeMedicationWithProration({
                patientId: user.id,
                newMedicationDosageId: newDosageId,
                oldMedicationDosageId: oldDosageId,
                changeReason: `Patient changed dosage from ${currentMed.dosage} to ${preferences.dosage}`,
                changedBy: 'patient'
              })
              
              if (changeResult.success) {
                console.log('✅ Automatic subscription change successful:', changeResult)
                
                // Mark medication as recently adjusted to show subscription adjusted component
                setRecentlyAdjustedMedication(preferences.medicationId || editingMedication?.id || '')
                
                // Enhanced success message with refund information
                const refundInfo = changeResult.refundAmount > 0 
                  ? ` A refund of $${changeResult.refundAmount.toFixed(2)} has been ${changeResult.refundIssued ? 'issued to your payment method' : 'credited to your account'}.`
                  : ''
                
                showToast({
                  title: 'Subscription Updated',
                  description: `Your ${currentMed.name} subscription has been updated automatically.${refundInfo}`,
                  variant: 'success'
                })
              } else {
                console.error('❌ Automatic subscription change failed:', changeResult.error)
                showToast({
                  title: 'Subscription Update Failed', 
                  description: `Your medication preference was saved, but automatic subscription update failed: ${changeResult.error}`,
                  variant: 'warning'
                })
              }
            } else {
              console.log('ℹ️ Skipping subscription change - no existing subscription or same dosage')
            }
          } catch (subscriptionError) {
            console.error('❌ Error during automatic subscription change:', subscriptionError)
            showToast({
              title: 'Subscription Update Error',
              description: 'Your medication preference was saved, but there was an error updating your subscription automatically.',
              variant: 'warning'
            })
          }
        }
        
        // Show success toast for new medications or when no subscription change needed
        if (!preferences.isEdit) {
          const medicationName = editingMedication?.medicationName || 'Medication'
          showToast({
            title: 'Preference Added',
            description: `${medicationName} preference has been added and is pending provider review`,
            variant: 'success'
          })
        }
        
        // Refresh medication data (but don't let it override onboarding state if user is mid-onboarding)
        const wasOnboarded = isOnboarded
        await fetchPostOnboardingData()
        
        // If user was in onboarding before refresh but fetchPostOnboardingData set them as onboarded,
        // revert back to onboarding mode since they haven't completed all checklist steps yet
        if (!wasOnboarded && isOnboarded) {
          console.log('🔄 Reverting premature onboarding completion - user still needs to complete all steps')
          setIsOnboarded(false)
          setOnboardingStatusLoading(false)
        }
        
        // Mark medication checklist item as completed
        setChecklistItems(prev => {
          const updatedItems = prev.map(i => 
            i.id === 'medication' ? { ...i, completed: true } : i
          )
          
          // Check if all essential checklist items are now completed
          // Note: Only complete onboarding when user has truly finished all steps
          const planCompleted = updatedItems.find(i => i.id === 'plan')?.completed || false
          const medicationCompleted = updatedItems.find(i => i.id === 'medication')?.completed || false
          const appointmentCompleted = updatedItems.find(i => i.id === 'appointment')?.completed || false
          
          console.log('📋 Onboarding progress check:', {
            plan: planCompleted,
            medication: medicationCompleted, 
            appointment: appointmentCompleted
          })
          
          // Only complete onboarding when ALL three core steps are truly finished
          const allEssentialCompleted = planCompleted && medicationCompleted && appointmentCompleted
          if (allEssentialCompleted) {
            console.log('✅ All essential onboarding steps completed - transitioning to onboarded state')
            markOnboardingCompleted()
          } else {
            console.log('⏳ Onboarding in progress - still need to complete remaining steps')
          }
          
          return updatedItems
        })
        
        // Close dialog and reset editing state
        setMedicationDialogOpen(false)
        setEditingMedication(undefined)
      } else {
        console.error('❌ Failed to save medication preference:', result.error)
        showToast({
          title: 'Update Failed',
          description: result.error?.message || 'Failed to save medication preference',
          variant: 'error'
        })
      }
    } catch (error) {
      console.error('❌ Exception saving medication preference:', error)
      showToast({
        title: 'Error',
        description: 'An unexpected error occurred while saving your preference',
        variant: 'error'
      })
    } finally {
      setMedicationLoading(false)
    }
  }

  const handleAddAnotherMedication = async (preferences: { medicationId: string; dosage: string }) => {
    if (!user?.id) return

    setMedicationLoading(true)
    try {
      console.log('🔄 Adding additional medication preference')
      const result = await authService.createMedicationPreference({
        medicationId: preferences.medicationId,
        dosage: preferences.dosage,
        userId: user.id
      })
      
      if (result.success) {
        console.log('✅ Additional medication preference added successfully')
        
        // Refresh medication data to show new card (keeping existing ones visible)
        await fetchPostOnboardingData()
        
        // Keep dialog open for more additions
        // Form will be reset automatically by the dialog component
        // Note: The dashboard will show all medication cards including the new one
      } else {
        console.error('❌ Failed to add medication preference:', result.error)
      }
    } catch (error) {
      console.error('❌ Exception adding medication preference:', error)
    } finally {
      setMedicationLoading(false)
    }
  }
  
  const handleAppointmentBooking = async (appointmentData: {
    patientProfileId: string
    providerId: string
    appointmentDate: string
    startTime: string
    treatmentType: string
    medicationPreferenceId: string
    appointmentType: string
    patientNotes?: string
  }) => {
    if (!user?.id) return { success: false, message: 'User not authenticated' }
    
    setAppointmentLoading(true)
    try {
      console.log('🔄 Booking appointment:', appointmentData)
      
      const result = await authService.bookAppointment({
        patientProfileId: user.id,
        providerId: appointmentData.providerId,
        appointmentDate: appointmentData.appointmentDate,
        startTime: appointmentData.startTime,
        treatmentType: appointmentData.treatmentType,
        appointmentType: appointmentData.appointmentType,
        patientNotes: appointmentData.patientNotes
      })
      
      if (result.success) {
        console.log('✅ Appointment booked successfully')
        
        // Mark appointment checklist item as completed
        setChecklistItems(prev => {
          const updatedItems = prev.map(i => 
            i.id === 'appointment' ? { ...i, completed: true } : i
          )
          
          // Check if all essential checklist items are now completed
          const planCompleted = updatedItems.find(i => i.id === 'plan')?.completed || false
          const medicationCompleted = updatedItems.find(i => i.id === 'medication')?.completed || false
          const appointmentCompleted = updatedItems.find(i => i.id === 'appointment')?.completed || false
          
          console.log('📋 Onboarding progress check after appointment booking:', {
            plan: planCompleted,
            medication: medicationCompleted, 
            appointment: appointmentCompleted
          })
          
          // Only complete onboarding when ALL three core steps are truly finished
          const allEssentialCompleted = planCompleted && medicationCompleted && appointmentCompleted
          if (allEssentialCompleted) {
            console.log('✅ All essential onboarding steps completed - transitioning to onboarded state')
            markOnboardingCompleted()
          } else {
            console.log('⏳ Onboarding in progress - still need to complete remaining steps')
          }
          
          return updatedItems
        })
        
        // Close dialog
        setAppointmentDialogOpen(false)
        
        // Refresh appointment data to show the new appointment and hide prescription alerts
        console.log('🔄 Refreshing appointment data after successful booking')
        await fetchPostOnboardingData()
        
        return { success: true, message: 'Appointment booked successfully', appointmentId: result.appointmentId }
      } else {
        console.error('❌ Failed to book appointment:', result.error)
        return { success: false, message: result.error?.message || 'Failed to book appointment' }
      }
    } catch (error) {
      console.error('❌ Exception booking appointment:', error)
      return { success: false, message: 'An error occurred while booking your appointment' }
    } finally {
      setAppointmentLoading(false)
    }
  }
  
  const handleGetAvailableSlots = async (
    providerId: string,
    startDate: string,
    endDate: string,
    treatmentType?: string
  ) => {
    try {
      console.log('🔄 Getting available slots for provider:', providerId)
      return await authService.getAvailableSlots(providerId, startDate, endDate, treatmentType)
    } catch (error) {
      console.error('❌ Exception getting available slots:', error)
      return { data: [], error }
    }
  }

  const handleRescheduleAppointment = async (appointmentData: {
    appointmentId: string
    appointmentDate: string
    startTime: string
  }) => {
    if (!user?.id) return { success: false, message: 'User not authenticated' }
    
    setAppointmentLoading(true)
    try {
      console.log('🔄 Rescheduling appointment:', appointmentData)
      
      const result = await authService.rescheduleAppointment(
        appointmentData.appointmentId,
        appointmentData.appointmentDate,
        appointmentData.startTime,
        'patient',
        user?.id,
        'Patient requested reschedule via web interface'
      )
      
      if (result.success) {
        console.log('✅ Appointment rescheduled successfully')
        
        // Refresh appointment data
        await fetchPostOnboardingData()
        
        // Close dialog and reset reschedule state
        setAppointmentDialogOpen(false)
        setIsRescheduleMode(false)
        setAppointmentToReschedule(undefined)
        
        // Note: Success notification is handled by visits-booking-dialog.tsx
        // to avoid duplicate notifications with timezone-aware details
        
        return { success: true, message: 'Appointment rescheduled successfully' }
      } else {
        console.error('❌ Failed to reschedule appointment:', result.error)
        return { success: false, message: result.error?.message || 'Failed to reschedule appointment' }
      }
    } catch (error) {
      console.error('❌ Exception rescheduling appointment:', error)
      return { success: false, message: 'An error occurred while rescheduling your appointment' }
    } finally {
      setAppointmentLoading(false)
    }
  }



  const handleNavigate = (itemOrUrl: string) => {
    console.log('Navigation clicked:', itemOrUrl)
    // Handle both item names and URLs
    if (itemOrUrl.startsWith('/')) {
      // It's a URL, navigate directly
      navigate(itemOrUrl)
    } else {
      // It's an item name, handle accordingly
      switch (itemOrUrl) {
        case 'Treatments':
          navigate('/treatments')
          break
        case 'Messaging':
          navigate('/messaging')
          break
        case 'Dashboard':
          navigate('/dashboard')
          break
        case 'Tracking':
          navigate('/treatments')
          break
        default:
          console.log('Unknown navigation item:', itemOrUrl)
      }
    }
  }

  // Account dialog state
  const [accountDialogOpen, setAccountDialogOpen] = useState(false)
  const [accountDialogSection, setAccountDialogSection] = useState('Account')
  
  // Support/Feedback dialog state
  const [supportFeedbackDialogOpen, setSupportFeedbackDialogOpen] = useState(false)
  const [supportFeedbackType, setSupportFeedbackType] = useState<'support' | 'feedback'>('support')

  const handleAccountClick = () => {
    setAccountDialogSection('Account')
    setAccountDialogOpen(true)
  }

  const handleHealthProfileClick = () => {
    setAccountDialogSection('Health Profile')
    setAccountDialogOpen(true)
  }

  const handleBillingClick = () => {
    setAccountDialogSection('Billing and Plans')
    setAccountDialogOpen(true)
  }

  const handlePreferencesClick = () => {
    setAccountDialogSection('Preferences')
    setAccountDialogOpen(true)
  }

  const handleAddShippingAddressClick = () => {
    setAccountDialogSection('Add Shipping Address')
    setAccountDialogOpen(true)
  }

  const handleSupportFeedbackClick = (type: "Support" | "Feedback") => {
    console.log('🎯 handleSupportFeedbackClick called with type:', type)
    console.log('🎯 Current supportFeedbackDialogOpen state:', supportFeedbackDialogOpen)
    setSupportFeedbackType(type.toLowerCase() as 'support' | 'feedback')
    setSupportFeedbackDialogOpen(true)
    console.log('🎯 Set supportFeedbackDialogOpen to true')
  }

  const handleSupportFeedbackSubmit = async (message: string, type: 'support' | 'feedback') => {
    if (!user?.email) {
      return { 
        success: false, 
        error: 'User email not available' 
      }
    }

    try {
      const result = await emailService.sendSupportFeedback({
        type,
        message,
        userEmail: user.email,
        userName: user.user_metadata?.full_name || user.email.split('@')[0] || 'Anonymous User'
      })

      return result
    } catch (error) {
      console.error('Failed to send support/feedback message:', error)
      return {
        success: false,
        error: 'Failed to send message'
      }
    }
  }

  // Extract user data for the dashboard
  const userData = user ? {
    id: user.id, // Include the user ID for payment functionality
    name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'Patient',
    email: user.email || '',
    avatar: user.user_metadata?.avatar_url || ''
  } : undefined

  // Debug current state
  console.log('🔍 PatientDashboard render - isOnboarded:', isOnboarded)
  console.log('🔍 PatientDashboard render - realMedicationData:', realMedicationData)
  console.log('🔍 PatientDashboard render - realMedicationData.length:', realMedicationData?.length)
  console.log('🔍 PatientDashboard render - onboardingStatusLoading:', onboardingStatusLoading, 'isOnboarded:', isOnboarded)

  // Show loading state while determining onboarding status
  if (onboardingStatusLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader />
      </div>
    )
  }

  return (
    <>
      <SidebarPatientDashboard 
        user={userData} 
        onLogout={handleLogout}
        isOnboarded={isOnboarded}
        checklistItems={!isOnboarded ? checklistItems : undefined}
        onChecklistItemClick={handleChecklistItemClick}
        medications={!isOnboarded ? [sampleMedication] : undefined}
        appointment={undefined} // Use only real appointment data, not sample data
        onRescheduleAppointment={() => {
          // This wrapper matches the expected signature
          console.log('Reschedule appointment clicked')
        }}
        onMedicationAction={handleMedicationAction}
        onNavigate={handleNavigate}
        // Simple card data for post-onboarding - mark recently adjusted medications
        realMedicationData={realMedicationData?.map(med => ({
          ...med,
          isRecentlyAdjusted: recentlyAdjustedMedication === med.id
        }))}
        isFreePlan={isFreePlan}
        realAppointmentData={isFreePlan ? [] : realAppointmentData}
        onEditMedication={handleEditMedication}
        onEditAppointment={handleEditAppointment}
        onSubscriptionAdjustedOk={handleSubscriptionAdjustedOk}
        onSubscriptionAdjustedView={handleSubscriptionAdjustedView}
        onAddMedication={() => handleEditMedication()}
        onAccountClick={handleAccountClick}
        onBillingClick={handleBillingClick}
        onPreferencesClick={handlePreferencesClick}
        onHealthProfileClick={handleHealthProfileClick}
        onSupportFeedbackClick={handleSupportFeedbackClick}
        onAddShippingAddress={handleAddShippingAddressClick}
        patientAddresses={patientAddresses}
        onOpenAppointmentDialog={handleOpenAppointmentDialog}
      />
      <MedicationPreferencesDialog
        open={medicationDialogOpen}
        onOpenChange={(open: boolean) => {
          setMedicationDialogOpen(open)
          if (!open) {
            setEditingMedication(undefined)
          }
        }}
        medications={availableMedications}
        onSubmit={handleMedicationPreferencesSubmit}
        onAddAnother={handleAddAnotherMedication}
        loading={medicationLoading}
        editMode={!!editingMedication}
        isOnboarding={!isOnboarded}
        currentMedication={editingMedication}
      />
      <VisitsBookingDialog
        open={appointmentDialogOpen}
        onOpenChange={(open: boolean) => {
          setAppointmentDialogOpen(open)
          if (!open) {
            // Reset reschedule state when dialog closes
            setIsRescheduleMode(false)
            setAppointmentToReschedule(undefined)
          }
        }}
        patientProfileId={user?.id || ''}
        medicationPreferences={medicationPreferences}
        providers={providers}
        onBookAppointment={handleAppointmentBooking}
        onGetAvailableSlots={handleGetAvailableSlots}
        isRescheduleMode={isRescheduleMode}
        existingAppointment={appointmentToReschedule}
        onRescheduleAppointment={handleRescheduleAppointment}
      />
      <AccountDialog
        open={accountDialogOpen}
        onOpenChange={(open) => {
          setAccountDialogOpen(open)
          // When account dialog closes, recheck phone number to potentially hide the alert
          if (!open) {
            // Small delay to ensure any phone number changes are saved first
            setTimeout(() => {
              // This will trigger the phone number check hook to revalidate
              window.dispatchEvent(new CustomEvent('phoneNumberUpdate'))
            }, 1000)
          }
        }}
        activeSection={accountDialogSection}
        onSectionChange={setAccountDialogSection}
        onAddressChange={async () => {
          console.log('📍 Address changed - reloading patient addresses')
          await loadPatientAddresses()
        }}
        onSupportClick={() => handleSupportFeedbackClick('Support')}
      />
      <SupportFeedbackDialog
        open={supportFeedbackDialogOpen}
        onOpenChange={setSupportFeedbackDialogOpen}
        type={supportFeedbackType}
        onSubmit={handleSupportFeedbackSubmit}
      />
    </>
  )
}